import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { request } from "../../../common/api";
import { GET_BACKUP_SETTING } from "../../../common/query-keys";
import { showAlert } from "../../../components/containers/Alert";
import { PageContent } from "../../../components/organisms/Contents/PageContent";
import { DBBackupSection } from "../../../components/organisms/DBBackupSection";
import { ModalLoading } from "../../../components/molecules/Modals/ModalLoading";

interface AutoBackupInputs {
  bupAutoEnabled?: boolean;
  bupAutoInterval?: number;
}

interface ManualBackupInputs {
  startTime?: Date;
  endTime?: Date;
}

export const BackupPage = () => {
  const [backupLoading, setBackupLoading] = useState<boolean>(false);
  const [manualBackupInputs, setManualBackupInputs] =
    useState<ManualBackupInputs>({
      startTime: undefined,
      endTime: undefined,
    });

  const [autoBackupInputs, setAutoBackupInputs] = useState<AutoBackupInputs>({
    bupAutoEnabled: undefined,
    bupAutoInterval: undefined,
  });

  useQuery({
    queryKey: [GET_BACKUP_SETTING],
    queryFn: async () => {
      const result = await request<AutoBackupInputs>({
        method: "GET",
        url: "/backup-setting",
      });

      setAutoBackupInputs(result);

      return result;
    },
  });

  // DB 수동 백업
  const { mutateAsync: manualBackup } = useMutation({
    mutationFn: (data: ManualBackupInputs) =>
      request({
        method: "POST",
        url: "/backup-setting/manual",
        data: data,
      }),

    onSuccess: () => {
      showAlert("DB 수동 백업이 완료되었습니다.");
    },
  });

  // DB 자동 백업
  const { mutateAsync: autoBackup } = useMutation({
    mutationFn: (data: AutoBackupInputs) =>
      request({
        method: "POST",
        url: "/backup-setting",
        data: data,
      }),

    onSuccess: () => {
      showAlert("DB 자동 백업 설정이 완료되었습니다.");
    },
  });

  const handleManualBackup = async () => {
    if (!manualBackupInputs?.startTime || !manualBackupInputs?.endTime) {
      return showAlert("시작 시간과 종료 시간을 입력해주세요.");
    }
    setBackupLoading(true);
    try {
      await manualBackup(manualBackupInputs);
    } catch (e: any) {
      setBackupLoading(false);
      showAlert("서버 요청 오류!");
      console.log("@ E: ", e);
    } finally {
      setBackupLoading(false);
    }
  };

  const handleAutoBackup = async () => {
    if (
      autoBackupInputs?.bupAutoEnabled === undefined ||
      !autoBackupInputs?.bupAutoInterval
    ) {
      return showAlert("자동 백업 활성화 여부와 주기를 입력해주세요.");
    }

    try {
      await autoBackup(autoBackupInputs);
    } catch (e: any) {
      showAlert("서버 요청 오류!");
      console.log("@ E: ", e);
    } finally {
      setBackupLoading(false);
    }
  };

  const handleChangeManualBackup = (
    name: "startTime" | "endTime",
    value: string
  ) => {
    setManualBackupInputs((prev) => ({
      ...prev,
      [name]: new Date(value),
    }));
  };

  const handleChangeAutoBackup = (
    name: "bupAutoEnabled" | "bupAutoInterval",
    value: string
  ) => {
    setAutoBackupInputs((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <PageContent
      depth01Title="관리자 설정"
      depth02Title="사용자 관리"
      depth03Title="DB 백업 설정"
    >
      <>
        <DBBackupSection
          autoBackupInputs={autoBackupInputs}
          onChangeManualBackup={handleChangeManualBackup}
          onChangeAutoBackup={handleChangeAutoBackup}
          onManualBackup={handleManualBackup}
          onAutoBackup={handleAutoBackup}
        />
        {backupLoading && <ModalLoading modalTitle="백업 중입니다..." />}
      </>
    </PageContent>
  );
};
